<template>
  <div :class="$style.stockInfo">
    <MoleculesConsumerHeading
      title="在庫状況"
      :sub-title="PRODUCT_IDS_IN_STOCK_INFO.find((item) => item.category_slug === categorySlug)?.category_title"
      align="left"
    />
    <MoleculesConsumerTabBar
      v-model="categorySlug"
      :list="
        PRODUCT_IDS_IN_STOCK_INFO.map((item) => ({
          key: item.category_slug,
          value: item.category_title,
        }))
      "
      @update:model-value="updateCategorySlug($event as CategorySlug)"
    />

    <div :class="$style.description">
      <div>
        <span>○</span>
        <span>：在庫あり</span>
      </div>
      <div>
        <span>▲</span>
        <span>：残りわずか</span>
      </div>
      <div>
        <span>○(予約)</span>
        <span>：予約購入可能</span>
      </div>
      <div>
        <span>✕(再)</span>
        <span>：在庫切れ(再入荷予定あり)</span>
      </div>
      <div>
        <span>×</span>
        <span>：在庫切れ</span>
      </div>
      <div>
        <span>―</span>
        <span>：展開なし</span>
      </div>
    </div>
    <div :class="$style.productList">
      <div v-for="product in products" :key="product.document_id" :class="$style.productItem">
        <AtomsConsumerLink :url="getSiteProductPageUrl(product)">
          <div :class="$style.productInfo">
            <div v-if="product.skus[0].main_image">
              <AtomsConsumerImage :src="product.skus[0].main_image" :alt="product.name" :width="80" />
            </div>
            <h3>{{ product.name }} {{ product.material }}</h3>
          </div>
        </AtomsConsumerLink>
        <table
          v-if="
            PRODUCT_IDS_IN_STOCK_INFO.find((item) => item.productIds.includes(product.document_id))?.category_slug ===
            'bedding'
          "
          :class="$style.skusTable"
        >
          <!-- コンフォーター -->
          <thead>
            <tr>
              <th>カラー</th>
              <th v-for="size in ['シングル', 'セミダブル', 'ダブル', 'クイーン']" :key="size">{{ size }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="sku in product.skus.filter(
                (sku, index, self) => self.findIndex((t) => t.color === sku.color) === index,
              )"
              :key="sku.document_id"
            >
              <td>
                <div :class="$style.colorBox">
                  <div :class="$style.color" :style="{ backgroundColor: colorName(sku.color) }"></div>
                  {{ sku.color }}
                </div>
              </td>
              <td v-for="size in ['シングル', 'セミダブル', 'ダブル', 'クイーン']" :key="size">
                {{ product.skus.find((s) => s.color === sku.color && s.size === size)?.stock_label ?? "―" }}
              </td>
            </tr>
          </tbody>
        </table>
        <table v-else-if="product.document_id === '66fb720103caac222dc41bdd'" :class="$style.skusTable">
          <!-- BAKUNE Soft Knit（サイズ展開が異なるため） -->
          <thead>
            <tr>
              <th>カラー</th>
              <th v-for="size in ['S-M', 'L-XL']" :key="size">{{ size }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="sku in product.skus.filter(
                (sku, index, self) => self.findIndex((t) => t.color === sku.color) === index,
              )"
              :key="sku.document_id"
            >
              <td>
                <div :class="$style.colorBox">
                  <div :class="$style.color" :style="{ backgroundColor: colorName(sku.color) }"></div>
                  {{ sku.color }}
                </div>
              </td>
              <td v-for="size in ['S-M', 'L-XL']" :key="size">
                {{ product.skus.find((s) => s.color === sku.color && s.size === size)?.stock_label ?? "―" }}
              </td>
            </tr>
          </tbody>
        </table>
        <table v-else :class="$style.skusTable">
          <!-- それ以外 -->
          <thead>
            <tr>
              <th>カラー</th>
              <th v-for="size in ['XS', 'S', 'M', 'L', 'XL', '2XL']" :key="size">{{ size }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="sku in product.skus.filter(
                (sku, index, self) => self.findIndex((t) => t.color === sku.color) === index,
              )"
              :key="sku.document_id"
            >
              <td>
                <div :class="$style.colorBox">
                  <div :class="$style.color" :style="{ backgroundColor: colorName(sku.color) }"></div>
                  {{ sku.color }}
                </div>
              </td>
              <td v-for="size in ['XS', 'S', 'M', 'L', 'XL', '2XL']" :key="size">
                {{ product.skus.find((s) => s.color === sku.color && s.size === size)?.stock_label ?? "―" }}
              </td>
            </tr>
          </tbody>
        </table>
        <div :class="$style.buttonBox">
          <AtomsConsumerLink :url="getSiteProductPageUrl(product)">
            <AtomsConsumerButton variant="primary" block>商品詳細及び予約購入はこちら</AtomsConsumerButton>
          </AtomsConsumerLink>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { getSiteProductPageUrl } from "~/utils/functions/product"
import { PRODUCT_IDS_IN_STOCK_INFO, type CategorySlug } from "~/const/product"
import { ProductWithStockLabelSkus } from "~/server/api/data/products/stock_info.get"

const route = useRoute()

const categorySlug = ref<CategorySlug>("recovery-wear")
if (route.query.category) categorySlug.value = route.query.category as CategorySlug

const products = ref<ProductWithStockLabelSkus[]>()
const productIds = ref(
  PRODUCT_IDS_IN_STOCK_INFO.find((item) => item.category_slug === categorySlug.value)?.productIds ?? [],
)

const updateCategorySlug = (value: CategorySlug) => {
  categorySlug.value = value
  productIds.value = PRODUCT_IDS_IN_STOCK_INFO.find((item) => item.category_slug === value)?.productIds ?? []
  navigateTo({ query: { category: value } })
}

const colorName = (color?: string) => {
  if (!color) return ""
  return colors[color] || color
}

const colors: { [key: string]: string } = {
  Beige: "#DFCDC1",
  "Dark-khaki": "#595541",
  "Light-gray": "#D3D3D3",
  "Coral-pink": "#BE8582",
  "Dark-gray": "#A9A9A9",
  "Off-white": "#FAF9F6",
  Brown: "#AB9D9A",
  "Light-green": "#C0C6BA",
  "Smoke-pink": "#CFB6B2",
  Greige: "#B5A8A0",
  Pink: "#DFC7C3",
}

watch(
  productIds,
  async (productIds) => {
    if (productIds.length) {
      const stockInfoData = await $fetch<{ products: ProductWithStockLabelSkus[] }>("/api/data/products/stock_info", {
        query: {
          productIds,
        },
      })
      const result = stockInfoData.products ?? []
      // productIds順に並び替える
      products.value = result.sort((a, b) => productIds.indexOf(a.document_id) - productIds.indexOf(b.document_id))
    }
  },
  { immediate: true },
)

useHead({
  title: `${
    PRODUCT_IDS_IN_STOCK_INFO.find((item) => item.category_slug === categorySlug.value)?.category_title
  }シリーズ在庫状況`,
  meta: [
    {
      name: "description",
      content: `TENTIALの${
        PRODUCT_IDS_IN_STOCK_INFO.find((item) => item.category_slug === categorySlug.value)?.category_title
      }シリーズの在庫状況が確認できます。`,
    },
  ],
})
</script>

<style scoped module lang="scss">
.stockInfo {
  @include container-lg;
  display: flex;
  flex-direction: column;
  gap: 20px;
  .description {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 0.5rem 1rem;
    background-color: $black-5;
    padding: 1rem;
    border-radius: 0.375rem;
    div {
      display: flex;
      align-items: center;
      gap: 0.25rem;
      span {
        font-size: $font-size-14;
      }
    }
  }
  .productList {
    display: flex;
    flex-direction: column;
    gap: 2.5rem;
    .productItem {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: 1rem;
      .productInfo {
        display: flex;
        align-items: center;
        gap: 1rem;
        img {
          border-radius: 0.375rem;
          overflow: hidden;
          aspect-ratio: 184 / 245;
        }
        h3 {
          font-size: $font-size-18;
        }
      }
      .skusTable {
        border-collapse: collapse;
        border-spacing: 0;
        thead {
          background-color: $secondary-10;

          font-size: $font-size-13;
          @include md {
            font-size: $font-size-16;
          }
        }
        tbody {
          tr {
            td {
              text-align: center;
              padding: 0.2rem;
              &:first-child {
                width: 100px;
              }
              &:nth-child(n + 2) {
                width: 80px;
                font-size: $font-size-11;
                @include md {
                  font-size: $font-size-16;
                }
              }
              .colorBox {
                display: flex;
                flex-direction: column;
                align-items: center;
                font-size: $font-size-12;
                .color {
                  width: 1.5rem;
                  height: 1.5rem;
                  border: 1px solid $black-20;
                }
              }
            }
          }
        }
      }
      .buttonBox {
        max-width: 400px;
        width: 100%;
        margin: 0 auto;
      }
    }
  }
}
</style>
